import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { db } from "../supabase";
import { useAngemeldet, useSitzung } from "../sitzung";
import { angemeldetBis } from "../speicher";
import { fehlertext } from "../fehler";
import { rollenEtiketten } from "../namen";
import { Avatar } from "../ui/Avatar";
import { Bestaetigung } from "../ui/Bestaetigung";
import { Feld } from "../ui/Feld";
import { FotoKnopf, useFoto } from "../ui/FotoKnopf";
import { Karte } from "../ui/Karte";
import { Knopf } from "../ui/Knopf";
import { Marke } from "../ui/Marke";
import { useHinweis } from "../ui/Hinweise";
import s from "./seiten.module.css";
import { NeueAufgabeKnopf } from "../ui/NeueAufgabeKnopf";
import { PushEinrichten } from "./PushEinrichten";

const DATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

/** Ansicht 17: Foto, Name, Aufgabe, Anmelde-Mail (nur lesen), Rolle (nur lesen), Abmelden. */
export function Profil() {
  const sitzung = useAngemeldet();
  const { neuLaden, abmelden } = useSitzung();
  const { zeigen } = useHinweis();
  const [vorname, setVorname] = useState(sitzung.person.vorname);
  const [name, setName] = useState(sitzung.person.name);
  const [aufgabe, setAufgabe] = useState(sitzung.person.aufgabe);
  const [speichert, setSpeichert] = useState(false);
  const foto = useFoto();
  const [ueberall, setUeberall] = useState(false);
  const bis = angemeldetBis();

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!vorname.trim()) {
      zeigen("Bitte trag deinen Vornamen ein.", { fehler: true });
      return;
    }
    setSpeichert(true);
    const { error } = await db().from("app_personen").update({ vorname: vorname.trim(), name: name.trim(), aufgabe: aufgabe.trim() }).eq("id", sitzung.person.id);
    setSpeichert(false);
    if (error) {
      zeigen(fehlertext(error), { fehler: true });
      return;
    }
    await neuLaden();
    zeigen("Gespeichert.");
  }

  return (
    <div className={s.stapel}>
      <div className={s.kopf}>
        <div>
          <h1>Profil.</h1>
          <p>So sehen dich die anderen in der App. Rolle und Zugänge ändern Admins.</p>
        </div>
        <NeueAufgabeKnopf />
      </div>
      <div className={s.raster}>
        <Karte titel="Foto" unterzeile="Freiwillig. JPG oder PNG, wird quadratisch zugeschnitten. Nur angemeldete Personen sehen dein Foto. Ohne Foto zeigt die App deine Initialen.">
          <div className={s.foto}>
            <Avatar person={sitzung.person} groesse={96} />
            <div className={s.zeile}>
              <FotoKnopf foto={foto} />
              {foto.hatFoto ? (
                <Knopf art="leise" onClick={() => void foto.entfernen()} disabled={foto.laeuft}>
                  Entfernen
                </Knopf>
              ) : null}
            </div>
          </div>
        </Karte>

        <Karte titel="Angaben">
          <form onSubmit={speichern} className={s.stapel}>
            <div className={s.zweiSpalten}>
              <Feld label="Vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} autoComplete="given-name" required maxLength={80} />
              <Feld label="Nachname" value={name} onChange={(e) => setName(e.target.value)} autoComplete="family-name" maxLength={80} />
            </div>
            <Feld label="Aufgabe im Verein" value={aufgabe} onChange={(e) => setAufgabe(e.target.value)} maxLength={120} />
            <Feld label="E-Mail für die Anmeldung" value={sitzung.email} readOnly hilfe="Ändert ein Admin, weil die Anmeldung daran hängt." />
            <div>
              <Knopf art="primaer" type="submit" disabled={speichert}>
                {speichert ? "Wird gespeichert …" : "Speichern"}
              </Knopf>
            </div>
          </form>
        </Karte>

        <Karte titel="Rolle" unterzeile="Niemand ändert seine eigene Rolle. Die Vorstandsrolle vergibt der Vorstand, Admin setzen andere Admins.">
          <div className={s.zeile}>
            {rollenEtiketten(sitzung.gruppen).map((r) => (
              <Marke key={r.label} art={r.label === "Admin" ? "dunkel" : "normal"}>
                {r.label}
              </Marke>
            ))}
          </div>
        </Karte>

        <Karte titel="Benachrichtigungen">
          <p>
            Was du in der App bekommst, steht unter <Link to="/mitteilungen">Mitteilungen</Link>. Dort schaltest du Mail und Push je Anlass ab. Zwischen 21 und 8 Uhr kommt keine Mail und keine Push.
          </p>
        </Karte>

        <PushEinrichten />

        <Karte titel="Anmeldung">
          <p>{bis ? `Auf diesem Gerät angemeldet bis ${DATUM.format(new Date(bis))}, danach brauchst du einen neuen Code.` : "Auf diesem Gerät angemeldet, bis du das Fenster schließt."}</p>
          <div className={s.zeile}>
            <Knopf onClick={() => void abmelden(false)}>Abmelden</Knopf>
            <Knopf art="gefahr" onClick={() => setUeberall(true)}>
              Überall abmelden
            </Knopf>
          </div>
          <p className={s.klein}>Überall abmelden, zum Beispiel nach Verlust des Handys.</p>
        </Karte>

        <Karte titel="Datenschutz">
          <p>
            Welche Daten die App verarbeitet, wer sie sieht und wie lange sie bleiben: <Link to="/datenschutz">Datenschutzhinweis</Link>.
          </p>
        </Karte>
      </div>
      <Bestaetigung
        offen={ueberall}
        titel="Auf allen Geräten abmelden?"
        bestaetigen="Überall abmelden"
        gefaehrlich
        onAbbrechen={() => setUeberall(false)}
        onBestaetigen={() => void abmelden(true)}
      >
        <p>Du wirst auf allen Geräten abgemeldet, auch hier. Danach meldest du dich mit einem neuen Code wieder an.</p>
      </Bestaetigung>
    </div>
  );
}
